import React, { useState } from 'react';
import { Card, Table, Select, Input, InputNumber, Button, Tag, Popconfirm, Popover, Modal, Tooltip, Typography, notification, Space } from 'antd';
import { PlusOutlined, DeleteOutlined, SaveOutlined, UndoOutlined, FileTextOutlined, FileTextFilled, AppstoreOutlined } from '@ant-design/icons';
import salesService from '../../../services/salesService';
import { getCurrencySymbol, formatMoney, matchesAllSearchWords } from './orderFormHelpers';
import AssortmentModal, { mapServerComponents, toPayloadComponents, sumBunches, sumStems } from './AssortmentModal';
import { lineSubtotal, hasOwnPrice, hasMultipleComponentPrices, fillMissingPrices } from './orderLineValue';

const isEmptyPrice = (value) => value === null || value === undefined || value === '';

const { Text } = Typography;

const InlineLinesTable = ({ orderId, orderLines, allProducts, clientCurrency, isReadOnly, onLinesChanged }) => {
  const [captureRow, setCaptureRow] = useState({
    product_variant_key: null,
    cantidad_cajas: null,
    ramos_por_caja: null,
    tallos_por_ramo: null,
    unit_price: null,
    billing_unit: 'TALLO',
    upc: '',
    mark_code: '',
    notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  // La lista de productos solo se abre cuando hay algo escrito.
  const [productSearch, setProductSearch] = useState('');

  const [rowEdits, setRowEdits] = useState({});
  const [savingRowId, setSavingRowId] = useState(null);

  // Detalle por línea: noteOpenId = fila con el Modal editable abierto;
  // hoverDetailId = fila con el preview de solo lectura por hover.
  // noteDraft / upcDraft son los borradores del Modal; al aplicar se
  // vuelcan a rowEdits (dirty).
  const [noteOpenId, setNoteOpenId] = useState(null);
  const [hoverDetailId, setHoverDetailId] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [upcDraft, setUpcDraft] = useState('');

  // Surtido: los componentes de la línea que se está capturando viven aquí
  // hasta que el usuario da "+ Agregar"; los de una línea ya agregada viven en
  // rowEdits[detailId].components. assortmentOpenId identifica qué surtido está
  // abierto: 'capture' para la barra, o el detail_id de una fila.
  // La confirmación va con el hook para que se monte dentro de la página y
  // tome su contexto (tema, idioma), como pide antd 6.
  const [modal, modalContextHolder] = Modal.useModal();

  const [captureComponents, setCaptureComponents] = useState([]);
  const [assortmentOpenId, setAssortmentOpenId] = useState(null);
  // Toda línea lleva precio unitario: Agregar no la inserta sin él.
  const [capturePriceMissing, setCapturePriceMissing] = useState(false);

  const handleAdd = async () => {
    const { product_variant_key, cantidad_cajas, ramos_por_caja, tallos_por_ramo, unit_price } = captureRow;
    const isAssorted = captureComponents.length > 0;

    if (unit_price === null || unit_price === undefined || unit_price === '') {
      setCapturePriceMissing(true);
      return;
    }

    // En una caja surtida ramos/caja sale del surtido y tallos/ramo no aplica,
    // así que esos dos campos no se exigen.
    if (!product_variant_key || !cantidad_cajas) {
      notification.error({ message: 'Completa todos los campos' });
      return;
    }
    if (!isAssorted && (!ramos_por_caja || !tallos_por_ramo)) {
      notification.error({ message: 'Completa todos los campos' });
      return;
    }
    const parts = String(product_variant_key).split('_');
    const product_id = parts[0];
    const variant_id = parts[1] && parts[1] !== 'base' ? parts[1] : null;

    const payload = {
      lote_id: null,
      product_id,
      variant_id,
      packaging_type: 'CAJA',
      quantity: cantidad_cajas,
      tallos_por_ramo: isAssorted ? null : tallos_por_ramo,
      ramos_por_caja: isAssorted ? sumBunches(captureComponents) : ramos_por_caja,
      unit_price,
      billing_unit: captureRow.billing_unit,
      upc: captureRow.upc || null,
      mark_code: captureRow.mark_code || null,
      notes: captureRow.notes?.trim() ? captureRow.notes : null,
    };

    if (isAssorted) {
      payload.components = toPayloadComponents(captureComponents);
    }

    setSubmitting(true);
    try {
      const res = await salesService.agregarLinea(orderId, payload);
      notification.success({ message: 'Línea agregada' });
      setCaptureRow({
        product_variant_key: null,
        cantidad_cajas: null,
        ramos_por_caja: null,
        tallos_por_ramo: null,
        unit_price: null,
        billing_unit: 'TALLO',
        upc: '',
        mark_code: '',
        notes: '',
      });
      setCaptureComponents([]);
      onLinesChanged(res.data);
    } catch (error) {
      notification.error({
        message: 'Error al agregar línea',
        description: error.response?.data?.mensaje || 'Error desconocido',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const captureIsAssorted = captureComponents.length > 0;
  const capturePriceFromComponents = captureIsAssorted && captureRow.billing_unit === 'TALLO';

  // Por RAMO o CAJA los componentes no llevan precio: pasar de TALLO a otra
  // unidad los quita, previa confirmación. `apply` recibe los componentes sin
  // precio, o null si no había que quitar nada.
  const confirmUnitChange = (currentUnit, nextUnit, components, apply) => {
    if (currentUnit !== 'TALLO' || nextUnit === 'TALLO' || !components.some(hasOwnPrice)) {
      apply(null);
      return;
    }
    modal.confirm({
      title: 'Cambiar la unidad de cobro',
      content: 'Al cambiar la unidad se quitarán los precios por componente.',
      okText: 'Cambiar unidad',
      cancelText: 'Cancelar',
      onOk: () => apply(components.map(c => ({ ...c, unit_price: null }))),
    });
  };

  // Contenido de UNA caja. En una caja surtida sale de los componentes; si no,
  // de los campos ramos/caja y tallos/ramo de la barra.
  const ramosPorCaja = captureIsAssorted
    ? sumBunches(captureComponents)
    : (Number(captureRow.ramos_por_caja) || 0);
  const tallosPorCaja = captureIsAssorted
    ? sumStems(captureComponents)
    : (Number(captureRow.ramos_por_caja) || 0) * (Number(captureRow.tallos_por_ramo) || 0);

  const totalRamosEstimado = (Number(captureRow.cantidad_cajas) || 0) * ramosPorCaja;
  const totalTallosEstimado = (Number(captureRow.cantidad_cajas) || 0) * tallosPorCaja;

  // Misma regla que el backend: un surtido por tallo suma sus componentes,
  // cada uno a su precio resuelto.
  const cajasEstimadas = Number(captureRow.cantidad_cajas) || 0;
  const subtotalEstimado = lineSubtotal({
    billing_unit: captureRow.billing_unit,
    unit_price: captureRow.unit_price,
    quantity: cajasEstimadas,
    total_stems: totalTallosEstimado,
    total_bunches: totalRamosEstimado,
    total_boxes: cajasEstimadas,
  }, captureComponents);

  // Franja de resumen: cada valor aparece apenas tiene sus datos y mientras tanto
  // muestra "—". El subtotal espera el precio y la cantidad de su unidad.
  const cajasCaptura = Number(captureRow.cantidad_cajas) || 0;
  const hasBoxes = cajasCaptura > 0;
  const hasBunches = hasBoxes && ramosPorCaja > 0;
  const hasStems = hasBunches && tallosPorCaja > 0;
  const hasUnitQuantity = { TALLO: hasStems, RAMO: hasBunches, CAJA: hasBoxes }[captureRow.billing_unit] || false;
  const hasSubtotal = hasUnitQuantity && (Number(captureRow.unit_price) || 0) > 0;
  const captureSummary = [
    { label: 'Cajas', value: hasBoxes ? cajasCaptura.toLocaleString('es-CO') : '—' },
    { label: 'Ramos', value: hasBunches ? totalRamosEstimado.toLocaleString('es-CO') : '—' },
    { label: 'Tallos', value: hasStems ? totalTallosEstimado.toLocaleString('es-CO') : '—' },
    { label: 'Subtotal', value: hasSubtotal ? formatMoney(subtotalEstimado, clientCurrency) : '—' },
  ];

  const getRowValue = (row, field) => {
    const detailId = row.detail_id || row.id;
    if (rowEdits[detailId] && rowEdits[detailId][field] !== undefined) {
      return rowEdits[detailId][field];
    }
    return row[field];
  };

  // Firma comparable de un surtido, para detectar cambios reales (los arrays
  // nunca son iguales por identidad).
  const componentsSignature = (list) =>
    JSON.stringify(
      (list || []).map(c => [
        String(c.component_product_id),
        String(c.component_variant_id ?? ''),
        Number(c.bunches),
        Number(c.stems_per_bunch),
        hasOwnPrice(c) ? Number(c.unit_price) : null,
      ])
    );

  // Componentes vigentes de una fila: el borrador si el usuario tocó el
  // surtido, si no los que vinieron del servidor.
  const getRowComponents = (row) => {
    const detailId = row.detail_id || row.id;
    const edited = rowEdits[detailId]?.components;
    if (edited !== undefined) return edited;
    return mapServerComponents(row.components);
  };

  const isRowDirty = (row) => {
    const detailId = row.detail_id || row.id;
    const edits = rowEdits[detailId];
    if (!edits) return false;
    return Object.keys(edits).some(field => {
      if (field === 'components') {
        return componentsSignature(edits.components) !== componentsSignature(mapServerComponents(row.components));
      }
      return edits[field] !== row[field];
    });
  };

  const updateRowField = (row, field, value) => {
    const detailId = row.detail_id || row.id;
    setRowEdits(prev => ({
      ...prev,
      [detailId]: {
        ...(prev[detailId] || {}),
        [field]: value,
      },
    }));
  };

  // Un surtido que ya era por tallo y sigue siéndolo. Si se guardó antes del
  // precio por componente, se cobraba al precio de la línea: sus componentes
  // sin precio toman ese. Uno que pasa de RAMO o CAJA a tallo no, porque su
  // precio de línea es por otra unidad.
  const isLegacyStemAssortment = (row) =>
    row.billing_unit === 'TALLO' && getRowValue(row, 'billing_unit') === 'TALLO';

  // Los componentes con los que se abre el surtido de una fila.
  const openRowComponents = (row) => {
    const components = getRowComponents(row);
    return isLegacyStemAssortment(row)
      ? fillMissingPrices(components, getRowValue(row, 'unit_price'))
      : components;
  };

  const cancelRowEdits = (row) => {
    const detailId = row.detail_id || row.id;
    setRowEdits(prev => {
      const next = { ...prev };
      delete next[detailId];
      return next;
    });
  };

  const saveRow = async (row) => {
    const detailId = row.detail_id || row.id;
    const edits = rowEdits[detailId] || {};
    const components = getRowComponents(row);
    const isAssorted = components.length > 0;

    const payload = {
      packaging_type: row.packaging_type || 'CAJA',
      quantity: edits.quantity !== undefined ? edits.quantity : row.quantity,
      ramos_por_caja: edits.bunches_per_box !== undefined ? edits.bunches_per_box : row.bunches_per_box,
      tallos_por_ramo: edits.stems_per_bunch !== undefined ? edits.stems_per_bunch : row.stems_per_bunch,
      unit_price: edits.unit_price !== undefined ? edits.unit_price : row.unit_price,
      billing_unit: edits.billing_unit !== undefined ? edits.billing_unit : row.billing_unit,
      notes: edits.notes !== undefined ? edits.notes : row.notes,
      upc: edits.upc !== undefined ? edits.upc : row.upc,
      mark_code: edits.mark_code !== undefined ? edits.mark_code : row.mark_code,
    };

    if (isAssorted) {
      payload.ramos_por_caja = sumBunches(components);
      payload.tallos_por_ramo = null;
    }

    // En un surtido por tallo el precio sale de los componentes; en lo demás
    // es obligatorio (0 vale).
    if (!(isAssorted && payload.billing_unit === 'TALLO') && isEmptyPrice(payload.unit_price)) {
      notification.error({ message: 'Ingrese el precio unitario' });
      return;
    }

    // Una línea surtida reenvía siempre su surtido: el backend solo recalcula
    // los totales del surtido cuando recibe components, y si se omite volvería
    // a calcularlos desde ramos/caja y tallos/ramo. Si el surtido no se tocó,
    // sus componentes sin precio van sin el campo: el backend los deja como
    // están en vez de escribirles uno.
    if (isAssorted || edits.components !== undefined) {
      const sent = edits.components !== undefined
        ? components
        : components.map(({ unit_price, ...c }) => (hasOwnPrice({ unit_price }) ? { ...c, unit_price } : c));
      payload.components = toPayloadComponents(sent);
    }

    setSavingRowId(detailId);
    try {
      await salesService.actualizarLinea(detailId, payload);
      notification.success({ message: 'Línea actualizada' });
      cancelRowEdits(row);
      onLinesChanged();
    } catch (error) {
      notification.error({
        message: 'Error al actualizar línea',
        description: error.response?.data?.mensaje || 'Error desconocido',
      });
    } finally {
      setSavingRowId(null);
    }
  };

  const labelStyle = { fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.8, color: 'var(--fc-text-secondary)', fontWeight: 500 };
  const fixedTagStyle = { padding: '4px 11px', border: '1px solid var(--fc-border)', borderRadius: 8, backgroundColor: '#f9fafb', color: 'var(--fc-text-secondary)', fontSize: 14 };

  // Bloque de solo lectura del detalle (nota + UPC). Reutilizado por el
  // preview de hover y por el Modal en modo isReadOnly. Lee del buffer de
  // edición vía getRowValue, con fallback al valor del servidor.
  const renderDetailReadOnly = (row) => {
    const noteVal = getRowValue(row, 'notes');
    const hasNote = noteVal != null && String(noteVal).trim() !== '';
    const upcVal = getRowValue(row, 'upc');
    const hasUpc = upcVal != null && String(upcVal).trim() !== '';
    return (
      <div style={{ maxWidth: 260 }}>
        <div style={{ ...labelStyle, marginBottom: 6 }}>Detalle</div>
        <div style={{ ...labelStyle, fontWeight: 400, marginBottom: 4 }}>Nota</div>
        <div style={{ whiteSpace: 'pre-wrap', color: 'var(--fc-text-primary)', marginBottom: 10 }}>
          {hasNote ? noteVal : <Text type="secondary">Sin nota</Text>}
        </div>
        <div style={{ ...labelStyle, fontWeight: 400, marginBottom: 4 }}>UPC</div>
        <div style={{ color: 'var(--fc-text-primary)' }}>
          {hasUpc ? upcVal : <Text type="secondary">Sin UPC</Text>}
        </div>
      </div>
    );
  };

  const applyDetail = (row) => {
    const cleanNote = noteDraft.trim();
    updateRowField(row, 'notes', cleanNote.length ? cleanNote : null);
    const cleanUpc = upcDraft.trim();
    updateRowField(row, 'upc', cleanUpc.length ? cleanUpc : null);
    setNoteOpenId(null);
  };

  const columns = [
    {
      title: 'PRODUCTO',
      key: 'producto',
      render: (_, r) => {
        const n = r.product?.name || '';
        const a = (r.lote?.variant || r.variant)?.attributes?.map(x => x.value?.value).join(' ') || '';
        const fullName = `${n} ${a}`.trim();
        const detailId = r.detail_id || r.id;
        const noteVal = getRowValue(r, 'notes');
        const hasNote = noteVal != null && String(noteVal).trim() !== '';
        const upcVal = getRowValue(r, 'upc');
        const hasUpc = upcVal != null && String(upcVal).trim() !== '';
        const hasDetail = hasNote || hasUpc;
        const rowComponents = getRowComponents(r);

        // Solo el nombre se encoge (con "…" y tooltip); la etiqueta Surtida y el
        // ícono de Detalle conservan su ancho y quedan siempre visibles.
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
            <Text strong ellipsis={{ tooltip: fullName }} style={{ flex: '0 1 auto', minWidth: 0 }}>
              {fullName}
            </Text>
            {rowComponents.length > 0 && (
              <Tooltip title="Ver surtido de esta caja">
                <Tag
                  color="green"
                  style={{ flex: 'none', cursor: 'pointer', margin: 0 }}
                  onClick={() => setAssortmentOpenId(detailId)}
                >
                  Surtida
                </Tag>
              </Tooltip>
            )}
            <Popover
              trigger="hover"
              mouseEnterDelay={0.3}
              open={hoverDetailId === detailId && noteOpenId === null}
              onOpenChange={(open) => setHoverDetailId(open ? detailId : null)}
              content={renderDetailReadOnly(r)}
            >
              <span
                role="button"
                tabIndex={0}
                title={hasDetail ? 'Ver / editar detalle' : 'Agregar detalle'}
                onClick={() => {
                  // El clic abre el Modal editable; el hover solo muestra el preview.
                  setHoverDetailId(null);
                  setNoteDraft(hasNote ? String(noteVal) : '');
                  setUpcDraft(hasUpc ? String(upcVal) : '');
                  setNoteOpenId(detailId);
                }}
                style={{ flex: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', lineHeight: 1 }}
              >
                {hasDetail
                  ? <FileTextFilled style={{ color: 'var(--fc-accent)', fontSize: 15 }} />
                  : <FileTextOutlined style={{ color: 'var(--fc-text-secondary)', fontSize: 15, opacity: 0.55 }} />}
              </span>
            </Popover>
          </div>
        );
      },
    },
    {
      title: <Tooltip title="Tipo de caja">T. CAJA</Tooltip>,
      dataIndex: 'packaging_type',
      render: (val) => <Tag>{val || 'CAJA'}</Tag>,
      width: 70,
    },
    {
      title: 'MARCA',
      key: 'mark_code',
      width: 90,
      render: (_, r) => (
        <Input
          size="small"
          value={getRowValue(r, 'mark_code') ?? ''}
          onChange={(e) => {
            const v = e.target.value;
            updateRowField(r, 'mark_code', v.trim() === '' ? null : v);
          }}
          disabled={isReadOnly}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'CAJAS',
      key: 'cajas',
      width: 72,
      align: 'center',
      render: (_, r) => (
        <InputNumber
          min={1}
          size="small"
          controls={false}
          value={getRowValue(r, 'quantity')}
          onChange={(v) => updateRowField(r, 'quantity', v)}
          disabled={isReadOnly}
          style={{ width: '100%' }}
        />
      ),
    },
    {
      title: 'RAMOS',
      key: 'ramos_caja',
      width: 72,
      align: 'center',
      render: (_, r) => {
        const rowComponents = getRowComponents(r);
        const assorted = rowComponents.length > 0;
        return (
          <InputNumber
            min={1}
            size="small"
            controls={false}
            value={assorted ? sumBunches(rowComponents) : getRowValue(r, 'bunches_per_box')}
            onChange={(v) => updateRowField(r, 'bunches_per_box', v)}
            disabled={isReadOnly || assorted}
            style={{ width: '100%' }}
          />
        );
      },
    },
    {
      title: 'TALLOS',
      key: 'tallos_ramo',
      width: 72,
      align: 'center',
      render: (_, r) => {
        const assorted = getRowComponents(r).length > 0;
        return (
          <InputNumber
            min={1}
            size="small"
            controls={false}
            value={assorted ? null : getRowValue(r, 'stems_per_bunch')}
            placeholder={assorted ? '—' : undefined}
            onChange={(v) => updateRowField(r, 'stems_per_bunch', v)}
            disabled={isReadOnly || assorted}
            style={{ width: '100%' }}
          />
        );
      },
    },
    {
      title: <Tooltip title="Precio unitario">PRECIO</Tooltip>,
      key: 'unit_price',
      width: 120,
      align: 'center',
      render: (_, r) => {
        const linePrice = getRowValue(r, 'unit_price');
        const components = getRowComponents(r);
        // En un surtido por tallo el precio de la línea sale de sus componentes.
        if (getRowValue(r, 'billing_unit') === 'TALLO' && components.length > 0) {
          return (
            <Tooltip title="Edita los precios en el surtido">
              <div>
                <Text>{formatMoney(linePrice, clientCurrency)}</Text>
                {hasMultipleComponentPrices(components, linePrice) && (
                  <Tag style={{ display: 'block', width: 'fit-content', margin: '2px auto 0', fontSize: 10, lineHeight: '16px', padding: '0 4px' }}>Múltiples</Tag>
                )}
              </div>
            </Tooltip>
          );
        }
        return (
          <InputNumber
            aria-label="Precio de la línea"
            min={0}
            step={0.01}
            precision={2}
            size="small"
            controls={false}
            prefix={getCurrencySymbol(clientCurrency)}
            value={linePrice}
            onChange={(v) => updateRowField(r, 'unit_price', v)}
            disabled={isReadOnly}
            style={{ width: '100%' }}
          />
        );
      },
    },
    {
      title: <Tooltip title="Unidad de precio">UND</Tooltip>,
      key: 'price_unit',
      width: 96,
      render: (_, r) => (
        <Select
          size="small"
          value={getRowValue(r, 'billing_unit') || 'CAJA'}
          onChange={(v) => confirmUnitChange(getRowValue(r, 'billing_unit'), v, getRowComponents(r), (cleared) => {
            updateRowField(r, 'billing_unit', v);
            if (cleared) {
              updateRowField(r, 'components', cleared);
              // El precio por tallo no sirve por ramo ni por caja: se escribe de nuevo.
              updateRowField(r, 'unit_price', null);
            }
          })}
          disabled={isReadOnly}
          style={{ width: '100%' }}
          options={[
            { value: 'TALLO', label: 'TALLO' },
            { value: 'RAMO', label: 'RAMO' },
            { value: 'CAJA', label: 'CAJA' },
          ]}
        />
      ),
    },
    {
      title: <Tooltip title="Total tallos">T. TALLOS</Tooltip>,
      dataIndex: 'total_stems',
      width: 80,
      align: 'right',
    },
    {
      title: 'SUBTOTAL',
      key: 'subtotal',
      render: (_, r) => <Text strong>{formatMoney(r.subtotal, clientCurrency)}</Text>,
      width: 140,
      align: 'right',
    },
    {
      title: '',
      key: 'acciones',
      width: 96,
      render: (_, r) => {
        if (isReadOnly) return null;
        const dirty = isRowDirty(r);
        const detailId = r.detail_id || r.id;
        return (
          <Space size={4}>
            <Tooltip title="Surtido de la caja">
              <Button
                size="small"
                icon={<AppstoreOutlined />}
                onClick={() => setAssortmentOpenId(detailId)}
              />
            </Tooltip>
            {dirty && (
              <>
                <Tooltip title="Guardar cambios">
                  <Button
                    type="primary"
                    size="small"
                    icon={<SaveOutlined />}
                    loading={savingRowId === detailId}
                    onClick={() => saveRow(r)}
                  />
                </Tooltip>
                <Tooltip title="Descartar cambios">
                  <Button
                    size="small"
                    icon={<UndoOutlined />}
                    onClick={() => cancelRowEdits(r)}
                  />
                </Tooltip>
              </>
            )}
            {!dirty && (
              <Popconfirm
                title="¿Eliminar línea?"
                onConfirm={async () => {
                  try {
                    await salesService.eliminarLinea(detailId);
                    notification.success({ message: 'Línea eliminada' });
                    onLinesChanged();
                  } catch (e) {
                    notification.error({
                      message: 'Error al eliminar',
                      description: e.response?.data?.mensaje || 'No se pudo eliminar la línea.',
                    });
                  }
                }}
              >
                <Button danger size="small" icon={<DeleteOutlined />} />
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  // La fila del Modal se deriva por id en cada render (nunca se guarda en
  // estado) para que refleje siempre el buffer vivo de rowEdits.
  const modalRow =
    noteOpenId != null
      ? (orderLines || []).find((r) => (r.detail_id || r.id) === noteOpenId)
      : null;

  // Fila cuyo surtido está abierto ('capture' = la barra de captura, que aún
  // no tiene fila).
  const assortmentRow =
    assortmentOpenId != null && assortmentOpenId !== 'capture'
      ? (orderLines || []).find((r) => (r.detail_id || r.id) === assortmentOpenId)
      : null;

  return (
    <Card
      variant="borderless"
      title={
        <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--fc-text-primary)' }}>
          Líneas de la orden
        </span>
      }
      style={{
        marginBottom: 24,
        borderRadius: 12,
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
        border: '1px solid var(--fc-border)',
        backgroundColor: 'var(--fc-surface)',
      }}
      styles={{
        header: { border: 'none', paddingTop: 20, paddingBottom: 4 },
        body: { paddingTop: 12 },
      }}
    >
      {!isReadOnly && (
        // Los 11 campos no caben en una fila a 1280 px, así que van en dos:
        // Producto toma el sobrante de la fila 1 y UPC, Marca y Notas bajan a la 2.
        // Los anchos fijos dejan espacio a las etiquetas, que no se parten.
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 1fr) 96px 64px 88px 96px 120px 88px auto', gap: 12, alignItems: 'end', padding: '12px 0' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={labelStyle}>PRODUCTO</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <Select
                showSearch={{ searchValue: productSearch, onSearch: setProductSearch, filterOption: matchesAllSearchWords }}
                open={productSearch.trim().length > 0}
                onOpenChange={(nextOpen) => { if (!nextOpen) setProductSearch(''); }}
                popupMatchSelectWidth={false}
                placeholder="Buscar producto..."
                value={captureRow.product_variant_key}
                onChange={(val) => setCaptureRow(prev => ({ ...prev, product_variant_key: val }))}
                options={allProducts.flatMap(p => {
                  if (!p.variants || p.variants.length === 0) {
                    return [{ value: `${p.product_id}_base`, label: p.name }];
                  }
                  return p.variants.map(v => {
                    const attrStr = v.attributes?.map(a => a.value?.value).join(' ') || '';
                    return { value: `${p.product_id}_${v.variant_id}`, label: attrStr ? `${p.name} ${attrStr}` : p.name };
                  });
                })}
                disabled={isReadOnly || !orderId}
                style={{ flex: 1, minWidth: 0 }}
              />
              <Tooltip title={captureRow.product_variant_key ? 'Surtir esta caja' : 'Elige primero el producto'}>
                <Button
                  icon={<AppstoreOutlined />}
                  onClick={() => setAssortmentOpenId('capture')}
                  disabled={isReadOnly || !orderId || !captureRow.product_variant_key}
                  type={captureIsAssorted ? 'primary' : 'default'}
                >
                  Surtir{captureIsAssorted ? ` (${captureComponents.length})` : ''}
                </Button>
              </Tooltip>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={labelStyle}>TIPO DE CAJA</span>
            <div style={fixedTagStyle}>CAJA</div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={labelStyle}>CAJAS</span>
            <InputNumber aria-label="Cajas" min={1} controls={false} value={captureRow.cantidad_cajas} onChange={(v) => setCaptureRow(prev => ({ ...prev, cantidad_cajas: v }))} disabled={isReadOnly || !orderId} style={{ width: '100%' }} />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={labelStyle}>RAMOS/CAJA</span>
            <InputNumber
              aria-label="Ramos por caja"
              min={1}
              controls={false}
              value={captureIsAssorted ? ramosPorCaja : captureRow.ramos_por_caja}
              onChange={(v) => setCaptureRow(prev => ({ ...prev, ramos_por_caja: v }))}
              disabled={isReadOnly || !orderId || captureIsAssorted}
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={labelStyle}>TALLOS/RAMO</span>
            <InputNumber
              aria-label="Tallos por ramo"
              min={1}
              controls={false}
              value={captureIsAssorted ? null : captureRow.tallos_por_ramo}
              placeholder={captureIsAssorted ? '—' : undefined}
              onChange={(v) => setCaptureRow(prev => ({ ...prev, tallos_por_ramo: v }))}
              disabled={isReadOnly || !orderId || captureIsAssorted}
              style={{ width: '100%' }}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={labelStyle}>PRECIO UNT</span>
            {/* El aviso va en un tooltip para no mover la fila de campos. En un
                surtido por tallo el precio sale de los componentes. */}
            <Tooltip
              open={capturePriceMissing || undefined}
              title={capturePriceMissing ? 'Ingrese el precio unitario' : (capturePriceFromComponents ? 'Edita los precios en el surtido' : null)}
            >
              <InputNumber
                aria-label="Precio unitario"
                min={0}
                step={0.01}
                precision={2}
                controls={false}
                prefix={getCurrencySymbol(clientCurrency)}
                status={capturePriceMissing ? 'error' : undefined}
                value={captureRow.unit_price}
                onChange={(v) => {
                  setCaptureRow(prev => ({ ...prev, unit_price: v }));
                  setCapturePriceMissing(false);
                }}
                disabled={isReadOnly || !orderId || capturePriceFromComponents}
                style={{ width: '100%' }}
              />
            </Tooltip>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={labelStyle}>UND PRECIO</span>
            <Select
              value={captureRow.billing_unit}
              onChange={(val) => confirmUnitChange(captureRow.billing_unit, val, captureComponents, (cleared) => {
                // El precio por tallo no sirve por ramo ni por caja: se escribe de nuevo.
                setCaptureRow(prev => ({ ...prev, billing_unit: val, unit_price: cleared ? null : prev.unit_price }));
                if (cleared) setCaptureComponents(cleared);
              })}
              disabled={isReadOnly || !orderId}
              style={{ width: '100%' }}
              options={[
                { value: 'TALLO', label: 'TALLO' },
                { value: 'RAMO', label: 'RAMO' },
                { value: 'CAJA', label: 'CAJA' },
              ]}
            />
          </div>

          <div style={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: '140px 160px 1fr', gap: 12, alignItems: 'end' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={labelStyle}>UPC</span>
              <Input
                maxLength={13}
                value={captureRow.upc}
                onChange={(e) => setCaptureRow(prev => ({ ...prev, upc: e.target.value.replace(/\D/g, '') }))}
                disabled={isReadOnly || !orderId}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={labelStyle}>Marca</span>
              <Input
                maxLength={40}
                value={captureRow.mark_code}
                onChange={(e) => setCaptureRow(prev => ({ ...prev, mark_code: e.target.value }))}
                disabled={isReadOnly || !orderId}
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span style={labelStyle}>NOTAS</span>
              <Input
                maxLength={255}
                value={captureRow.notes}
                onChange={(e) => setCaptureRow(prev => ({ ...prev, notes: e.target.value }))}
                disabled={isReadOnly || !orderId}
              />
            </div>
          </div>

          {/* Agregar se ubica al final de la fila 1, pero en el código va después de
              Notas para que el tabulador recorra los campos opcionales antes del botón. */}
          <div style={{ gridRow: 1, gridColumn: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {!orderId ? (
              <Tooltip title="Completa los datos generales primero">
                <Button type="primary" icon={<PlusOutlined />} disabled>Agregar</Button>
              </Tooltip>
            ) : (
              <Button type="primary" icon={<PlusOutlined />} loading={submitting} onClick={handleAdd}>Agregar</Button>
            )}
          </div>
        </div>
      )}

      {!isReadOnly && (
        <div
          aria-label="Resumen de la línea"
          style={{
            textAlign: 'right',
            fontSize: 13,
            color: 'var(--fc-text-secondary)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {captureSummary.map((item, index) => (
            <React.Fragment key={item.label}>
              {index > 0 && ' · '}
              <span style={{ whiteSpace: 'nowrap' }}>
                {item.label}{' '}
                <span style={{ fontWeight: 500, color: item.label === 'Subtotal' ? 'var(--fc-accent)' : 'var(--fc-text-primary)' }}>
                  {item.value}
                </span>
              </span>
            </React.Fragment>
          ))}
        </div>
      )}

      <div style={{ height: 1, backgroundColor: 'var(--fc-border)', margin: '12px 0' }} />

      <style>{`
        .floracore-row-dirty > td {
          background-color: var(--fc-accent-soft) !important;
        }
      `}</style>

      <Table
        columns={columns}
        dataSource={orderLines}
        rowKey={r => r.detail_id || r.id}
        pagination={false}
        tableLayout="fixed"
        size="small"
        // Anchos fijos (908) + 220 de mínimo para Producto, que se queda con el resto.
        // Así a 1280 px (1134 útiles) no aparece desplazamiento horizontal.
        scroll={{ x: 1128 }}
        locale={{ emptyText: 'No hay líneas todavía' }}
        rowClassName={(r) => isRowDirty(r) ? 'floracore-row-dirty' : ''}
      />

      <Modal
        open={noteOpenId !== null}
        onCancel={() => setNoteOpenId(null)}
        destroyOnHidden
        title="Detalle"
        footer={
          isReadOnly
            ? [
                <Button key="close" onClick={() => setNoteOpenId(null)}>Cerrar</Button>,
              ]
            : [
                <Button key="cancel" onClick={() => setNoteOpenId(null)}>Cancelar</Button>,
                <Button
                  key="apply"
                  type="primary"
                  onClick={() => modalRow && applyDetail(modalRow)}
                >
                  Aplicar
                </Button>,
              ]
        }
      >
        {modalRow &&
          (isReadOnly ? (
            renderDetailReadOnly(modalRow)
          ) : (
            <div>
              <div style={{ ...labelStyle, fontWeight: 400, marginBottom: 4 }}>Nota</div>
              <Input.TextArea
                autoFocus
                rows={3}
                maxLength={300}
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                placeholder="Escribe una nota para esta línea..."
              />
              <div style={{ ...labelStyle, fontWeight: 400, margin: '10px 0 4px' }}>UPC</div>
              <Input
                value={upcDraft}
                onChange={(e) => setUpcDraft(e.target.value)}
                placeholder="Código UPC"
              />
              <div style={{ fontSize: 11, color: 'var(--fc-text-secondary)', marginTop: 4 }}>
                Formato esperado: 12 o 13 dígitos.
              </div>
            </div>
          ))}
      </Modal>

      {modalContextHolder}

      {assortmentOpenId !== null && (
        <AssortmentModal
          open
          allProducts={allProducts}
          isReadOnly={isReadOnly}
          currency={clientCurrency}
          billingUnit={assortmentOpenId === 'capture' ? captureRow.billing_unit : assortmentRow && getRowValue(assortmentRow, 'billing_unit')}
          linePrice={assortmentOpenId === 'capture' ? captureRow.unit_price : assortmentRow && getRowValue(assortmentRow, 'unit_price')}
          quantity={assortmentOpenId === 'capture' ? captureRow.cantidad_cajas : assortmentRow && getRowValue(assortmentRow, 'quantity')}
          initialComponents={
            assortmentOpenId === 'capture'
              ? captureComponents
              : (assortmentRow ? openRowComponents(assortmentRow) : [])
          }
          onCancel={() => setAssortmentOpenId(null)}
          onSave={(components, result) => {
            // Si la línea no tenía precio, el surtido le asigna uno.
            const basePrice = result?.linePrice;
            if (assortmentOpenId === 'capture') {
              setCaptureComponents(components);
              if (basePrice !== undefined) {
                setCaptureRow(prev => ({ ...prev, unit_price: basePrice }));
                setCapturePriceMissing(false);
              }
            } else if (assortmentRow) {
              // Abrir un surtido no cambia nada: solo se guarda si el usuario tocó algo.
              const changed = componentsSignature(components) !== componentsSignature(openRowComponents(assortmentRow));
              if (changed) {
                updateRowField(assortmentRow, 'components', components);
                if (basePrice !== undefined) updateRowField(assortmentRow, 'unit_price', basePrice);
              }
            }
            setAssortmentOpenId(null);
          }}
        />
      )}
    </Card>
  );
};

export default InlineLinesTable;
